import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { usePageTransition } from '../../utils/pageTransitions';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';

const LegalNotice = () => {
    const pageTransition = usePageTransition();

    return (
        <>
            <div className="min-h-screen bg-[var(--color-background)]">
                <Header />
                <motion.div {...pageTransition} className="bg-slate-50 dark:bg-slate-900">
                    <Helmet>
                        <title>Mentions Légales - ORAZEK Investment</title>
                        <meta name="description" content="Mentions légales d'ORAZEK Investment. Informations sur l'éditeur du site et les conditions légales." />
                        <meta name="robots" content="index, follow" />
                    </Helmet>

                    {/* Hero Section */}
                    <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white py-20">
                        <div className="max-w-4xl mx-auto px-6">
                            <div className="flex items-center mb-6">
                                <Icon name="Scale" size={48} className="text-blue-400" />
                            </div>
                            <h1 className="text-4xl md:text-5xl font-bold mb-4">Mentions Légales</h1>
                            <p className="text-xl text-blue-200">Informations légales et éditoriales</p>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="max-w-4xl mx-auto px-6 py-16">
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8 md:p-12 space-y-8">

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Building2" size={24} className="mr-3 text-blue-600" />
                                    1. Identification de l'Éditeur
                                </h2>
                                <div className="space-y-3 text-slate-700 dark:text-slate-300">
                                    <p><strong>Raison sociale :</strong> ORAZEK Investment</p>
                                    <p><strong>Forme juridique :</strong> Société de services</p>
                                    <p><strong>Siège social :</strong> Kinshasa, République Démocratique du Congo</p>
                                    <p><strong>Email :</strong> <a href="mailto:orazekinvestment5@gmail.com" className="text-blue-600 hover:underline">orazekinvestment5@gmail.com</a></p>
                                    <p><strong>Téléphone :</strong> <a href="tel:+243818883531" className="text-blue-600 hover:underline">+243 81 888 3531</a></p>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="User" size={24} className="mr-3 text-blue-600" />
                                    2. Directeur de Publication
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    Le directeur de la publication du site est le représentant légal d'ORAZEK Investment.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Code" size={24} className="mr-3 text-blue-600" />
                                    3. Développement et Conception
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    Le site a été conçu et développé par l'équipe technique d'ORAZEK Investment.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Copyright" size={24} className="mr-3 text-blue-600" />
                                    4. Propriété Intellectuelle
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                    L'ensemble du contenu de ce site (structure, textes, logos, images, vidéos, etc.) est la propriété exclusive d'ORAZEK Investment, sauf mention contraire.
                                </p>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    Toute reproduction, représentation, modification, publication, adaptation de tout ou partie des éléments du site, quel que soit le moyen ou le procédé utilisé, est interdite, sauf autorisation écrite préalable d'ORAZEK Investment.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Shield" size={24} className="mr-3 text-blue-600" />
                                    5. Protection des Données Personnelles
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                    Conformément à la législation en vigueur en République Démocratique du Congo et aux standards internationaux de protection des données, ORAZEK Investment s'engage à respecter la confidentialité des informations personnelles collectées sur ce site.
                                </p>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    Pour plus d'informations sur le traitement de vos données personnelles, veuillez consulter notre <a href="/privacy-policy" className="text-blue-600 hover:underline font-medium">Politique de Confidentialité</a>.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Cookie" size={24} className="mr-3 text-blue-600" />
                                    6. Cookies
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                    Le site utilise des cookies pour améliorer l'expérience utilisateur et analyser le trafic. Les cookies sont de petits fichiers texte stockés sur votre appareil lorsque vous visitez notre site.
                                </p>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    Vous pouvez configurer votre navigateur pour refuser les cookies, mais cela pourrait affecter certaines fonctionnalités du site.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Link2" size={24} className="mr-3 text-blue-600" />
                                    7. Liens Hypertextes
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                    Le site peut contenir des liens vers d'autres sites web. ORAZEK Investment ne peut être tenu responsable du contenu de ces sites externes ni des dommages pouvant résulter de leur utilisation.
                                </p>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    La création de liens hypertextes vers ce site nécessite l'autorisation préalable d'ORAZEK Investment.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="AlertTriangle" size={24} className="mr-3 text-blue-600" />
                                    8. Limitation de Responsabilité
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                    ORAZEK Investment s'efforce d'assurer l'exactitude et la mise à jour des informations diffusées sur ce site. Cependant, nous ne pouvons garantir l'exactitude, la précision ou l'exhaustivité des informations mises à disposition.
                                </p>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    ORAZEK Investment ne saurait être tenu responsable :
                                </p>
                                <ul className="mt-4 space-y-2 text-slate-700 dark:text-slate-300">
                                    <li className="flex items-start">
                                        <Icon name="XCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Des erreurs ou omissions dans le contenu</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="XCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>De l'indisponibilité temporaire ou permanente du site</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="XCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Des dommages directs ou indirects résultant de l'utilisation du site</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="XCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Des intrusions malveillantes qui pourraient causer des dommages</span>
                                    </li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Scale" size={24} className="mr-3 text-blue-600" />
                                    9. Droit Applicable
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    Les présentes mentions légales sont régies par le droit de la République Démocratique du Congo. Tout litige relatif à l'utilisation du site sera de la compétence exclusive des tribunaux de Kinshasa.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Mail" size={24} className="mr-3 text-blue-600" />
                                    10. Contact
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                    Pour toute question concernant ces mentions légales ou signaler un problème sur le site, vous pouvez nous contacter :
                                </p>
                                <div className="p-6 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                                    <p className="text-slate-900 dark:text-white font-medium mb-2">ORAZEK Investment</p>
                                    <p className="text-slate-700 dark:text-slate-300">Kinshasa, République Démocratique du Congo</p>
                                    <p className="text-slate-700 dark:text-slate-300">Email : <a href="mailto:orazekinvestment5@gmail.com" className="text-blue-600 hover:underline">orazekinvestment5@gmail.com</a></p>
                                    <p className="text-slate-700 dark:text-slate-300">Téléphone : <a href="tel:+243818883531" className="text-blue-600 hover:underline">+243 81 888 3531</a></p>
                                </div>
                            </section>

                        </div>
                    </div>
                </motion.div>
                <Footer />
            </div>
        </>
    );
};

export default LegalNotice;

